import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';

import { ensureDesktopPushPermission } from '../components/pique/push-permission-prompt/enable';
import { pushPermissionPromptDismissedAtAtom } from '../components/pique/push-permission-prompt/state';
import { calendarEventsNotificationPreferenceAtom } from '../components/pique/setting-modal/atoms';
import {
  enhancedNotify,
  showNotificationPermissionPrompt,
} from '../components/shared/notification-utils';

export interface CalendarNotificationPreference {
  enabled: boolean;
  isProcessing: boolean;
  toggle: (enable?: boolean) => Promise<void>;
}

export function useCalendarNotificationPreference(): CalendarNotificationPreference {
  const [preference, setPreference] = useAtom(
    calendarEventsNotificationPreferenceAtom
  );
  const setPushPromptDismissedAt = useSetAtom(
    pushPermissionPromptDismissedAtAtom
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const toggle = useCallback(
    async (enable?: boolean) => {
      if (isProcessing) return;

      const currentEnabled = preference === 'yes';
      const shouldEnable = enable !== undefined ? enable : !currentEnabled;

      if (shouldEnable === currentEnabled) return;

      setIsProcessing(true);

      try {
        if (!environment.isTauri) {
          setPreference(shouldEnable ? 'yes' : 'no');
          return;
        }

        if (shouldEnable) {
          setPushPromptDismissedAt(null);
          const outcome = await ensureDesktopPushPermission();

          if (outcome === 'needs-settings') {
            await showNotificationPermissionPrompt({
              title: 'Notification Permission Required',
              message:
                'Please enable notifications for Pique in System Settings to receive meeting reminders.',
            });
            return;
          }

          if (outcome === 'granted') {
            setPreference('yes');
            await enhancedNotify.success({
              title: 'Notifications Enabled',
              message: 'Pique will notify you of upcoming meetings!',
              timeout: 8000,
            });
          }
        } else {
          setPreference('no');
        }
      } catch (error) {
        console.error('Failed to update notification settings:', error);
        await enhancedNotify.error({
          title: 'Settings Update Failed',
          message:
            'Unable to update notification preferences. Please try again.',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, preference, setPreference, setPushPromptDismissedAt]
  );

  return {
    enabled: preference === 'yes',
    isProcessing,
    toggle,
  };
}
